import Loading from '@/components/Loading';
import Radio from '@/components/UI/FormElements/Radio/Radio';
import Select from '@/components/UI/Select/Select';
import SuccessTick from '@/components/UI/SuccessTick';
import type { components } from '@/generated/internal-api/openapi-types';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError.ts';
import type { StudyApiId } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import type { FC } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import type { SingleValue } from 'react-select';

type ColonyImportType = components['schemas']['ColonyImportType.schema'];
type ExternalColonyImportV1 = components['schemas']['ExternalColonyImportV1.schema'];
type ImportIdField = keyof Omit<ExternalColonyImportV1, 'import_type'>;

interface IdTypeOption {
  value: ImportIdField;
  label: string;
}

const breedingIdOptions: Array<IdTypeOption> = [
  { value: 'animal_id', label: 'Animal number' },
  { value: 'order_id', label: 'Order number' },
  { value: 'project_id', label: 'Project number' },
];

const experimentalIdOptions: Array<IdTypeOption> = [
  { value: 'batch_id', label: 'Batch number' },
  { value: 'order_id', label: 'Order number' },
  { value: 'project_id', label: 'Project number' },
];

interface ImportAnimalsProps {
  onComplete: () => void;
  studyApiId: StudyApiId;
  testId?: string;
}

const ImportAnimals: FC<ImportAnimalsProps> = ({ studyApiId, onComplete, testId = 'import-animals' }) => {
  const dispatch = useDispatch();
  const [stockType, setStockType] = useState<ColonyImportType>('breeding');
  const [importIdField, setImportIdField] = useState<ImportIdField>('animal_id');
  const [value, setValue] = useState('');
  const [messages, setMessages] = useState<Array<string>>([]);
  const [errors, setErrors] = useState<Array<string>>([]);

  const { loading: importLoading, invoke: invokeImport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/external-colony/import',
    invokeOnInit: false,
    path: { studyApiId },
  });

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const onChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };
  const handleStockTypeChange = (newStockType: ColonyImportType) => {
    setStockType(newStockType);
    if (newStockType === 'breeding') {
      setImportIdField('animal_id');
    } else {
      setImportIdField('batch_id');
    }
    setValue('');
  };

  const handleIdFieldChange = (option: SingleValue<IdTypeOption>) => {
    if (_notNil(option)) {
      setImportIdField(option.value);
      setValue('');
    }
  };

  const currentIdOptions = stockType === 'breeding' ? breedingIdOptions : experimentalIdOptions;
  const selectedIdOption = currentIdOptions.find((option) => option.value === importIdField) ?? currentIdOptions[0];

  const onSubmit = async () => {
    setMessages([]);
    setErrors([]);

    try {
      const requestBody: ExternalColonyImportV1 = {
        import_type: stockType,
        [importIdField]: value,
      };
      const response = await invokeImport({
        body: requestBody,
        path: { studyApiId },
      });

      const responseBody = response.body;
      if (_notNil(responseBody) && responseBody.success === true) {
        const messages = [`${responseBody.animals} animals`, `${responseBody.cages} cages`];
        setMessages(messages);
      } else if (_notNil(responseBody) && _notNil(responseBody.errors)) {
        setErrors(responseBody.errors);
      }

      onComplete();
    } catch (error) {
      setMessages([]);
      ExceptionHandler.captureException(
        new InVivoError('Could not import from external colony', {
          cause: error,
          slug: 'import-external-animals',
        })
      );
    }
  };

  return (
    <ModalContainer size="small" data-testid={testId}>
      <ModalHeader title="Import from tick@lab" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      {importLoading ? (
        <Loading txt="Importing from tick@lab" />
      ) : (
        <div className="pv3 ph3" data-testid={`${testId}__form`}>
          <div className="mb3">
            <Radio
              id="breeding"
              key="breeding"
              checked={stockType === 'breeding'}
              onChange={() => handleStockTypeChange('breeding')}
              name="stockType"
              label="Breeding stock"
              value="breeding"
              className="mb2"
              dataTestId={`${testId}__stock-type-breeding`}
            />
            {stockType === 'breeding' && (
              <div className="ml4 mb3" style={{ width: '340px' }} data-testid={`${testId}__breeding-options`}>
                <Select<IdTypeOption>
                  className="w-100 mb2"
                  options={breedingIdOptions}
                  value={selectedIdOption}
                  onChange={handleIdFieldChange}
                  isMulti={false}
                  isSearchable={false}
                  data-testid={`${testId}__id-type-select`}
                />
                <input
                  type="text"
                  placeholder="Number"
                  onChange={onChangeValue}
                  value={value}
                  autoFocus={true}
                  className="w-100"
                  data-testid={`${testId}__id-input`}
                />
              </div>
            )}
          </div>

          <div className="mb3">
            <Radio
              id="experimental"
              key="experimental"
              checked={stockType === 'experimental'}
              onChange={() => handleStockTypeChange('experimental')}
              name="stockType"
              label="Experimental stock"
              value="experimental"
              className="mb2"
              dataTestId={`${testId}__stock-type-experimental`}
            />
            {stockType === 'experimental' && (
              <div className="ml4 mb3" style={{ width: '340px' }} data-testid={`${testId}__experimental-options`}>
                <Select<IdTypeOption>
                  className="w-100 mb2"
                  options={experimentalIdOptions}
                  value={selectedIdOption}
                  onChange={handleIdFieldChange}
                  isMulti={false}
                  isSearchable={false}
                  data-testid={`${testId}__id-type-select`}
                />
                <input
                  type="text"
                  placeholder="Number"
                  onChange={onChangeValue}
                  value={value}
                  autoFocus={true}
                  className="w-100"
                  data-testid={`${testId}__id-input`}
                />
              </div>
            )}
          </div>

          {_isNotEmpty(messages) && (
            <div className="center mw6 ui-card" data-testid={`${testId}__success-message`}>
              <div className="pa3 bg-white">
                <h3 className="f4 mb3 flex flex-row items-center">
                  <SuccessTick className="mr2" />
                  <span>Import complete</span>
                </h3>
                <p className="f5 pb1">We imported the following entities from tick@lab</p>
                <ul>
                  {messages.map((message, index) => (
                    <li key={index} className="f5">
                      &#8226; {message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {_isNotEmpty(errors) && (
            <div className="w-100 h-100 flex items-center justify-center" data-testid={`${testId}__error-message`}>
              <ApiErrorBanner
                className="mb4 mw6"
                title="There was an error importing from tick@lab"
                text={errors.map((error) => `• ${error}`).join('\n')}
                error={errors}
              />
            </div>
          )}
        </div>
      )}
      <ModalActions
        onSubmit={onSubmit}
        submitButtonProps={{ disabled: _isEmpty(value), 'data-testid': `${testId}__submit-button` }}
        submitBtnText="Import"
        className="pa3 bt"
        onCancel={closeModal}
        data-testid={`${testId}__actions`}
      />
    </ModalContainer>
  );
};

export default ImportAnimals;
